import { useEffect, useState } from 'react';
import { Badge } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { InboxCountSchema } from '@tinypro-audit/contracts';
import { fetchJson } from '../api/client';

/** A sidebar count must never block navigation or make a page load fail. */
export function InboxCount({ identity }: { identity: string }) {
  const { pathname } = useLocation();
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    setCount(null);
  }, [identity]);
  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;
    let busy = false;
    let timer: ReturnType<typeof setTimeout>;
    const update = async () => {
      if (stopped || busy) return;
      busy = true;
      try {
        if (!document.hidden) {
          const next = await fetchJson(
            '/api/projects/workflow/count',
            { signal: controller.signal },
            InboxCountSchema,
          );
          if (!stopped) setCount(next.count);
        }
      } catch {
        // Keep the last known count during transient failures. Page data has its own errors.
      } finally {
        busy = false;
        if (!stopped) timer = setTimeout(() => void update(), 30_000);
      }
    };
    const onVisible = () => {
      if (!document.hidden) {
        clearTimeout(timer);
        void update();
      }
    };
    void update();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [identity, pathname]);
  return count !== null ? (
    <Badge
      size="sm"
      variant="light"
      radius="xl"
      title="Review entries in the default Inbox view"
    >
      {count}
    </Badge>
  ) : null;
}
